// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Container } from 'unstated';

import type { ContentStateState, ContentState } from './types.ts';
import type { EditorContentStateQuery$data } from './__generated__/EditorContentStateQuery.graphql.ts';

export const defaultValues: ContentStateState = {
	contentState: null,
	contentStateLastUpdated: null,
	shouldRunContentStateEffect: true,
	defaultStatesArray: [],
	customStatesArray: [],
	isContentStateEnabled: true,
	isSuggestedEnabled: true,
	isCustomEnabled: true,
	isUpdating: false,
	lastFetchedIsDraft: null,
	lastFetchedContentId: null,
};
export class ContentStateStateContainer extends Container<ContentStateState> {
	state: {
		contentState: ContentState | null;
		contentStateLastUpdated: string | null;
		shouldRunContentStateEffect: boolean;
		defaultStatesArray: (ContentState | null)[];
		customStatesArray: (ContentState | null)[];
		isContentStateEnabled: boolean;
		isSuggestedEnabled: boolean;
		isCustomEnabled: boolean;
		isUpdating: boolean;
		lastFetchedIsDraft: boolean | null;
		lastFetchedContentId: string | null;
	} = { ...defaultValues };

	// Guards optimistic, mutation-owned fields (applied status + option list) from being
	// clobbered by stale refetches while an operation is pending. Kept off `state` so it
	// survives the refetch-driven `setInitialValues` calls.
	isOptimisticOperationPending = false;

	setIsOptimisticOperationPending = (isOptimisticOperationPending: boolean): void => {
		this.isOptimisticOperationPending = isOptimisticOperationPending;
	};

	// The contentId for which the empty add-status trigger should be kept visible after a
	// view-mode delete. Stored as an id (not a boolean) so render can scope it to the current page
	// without a navigation listener — it's only honored while this matches the active contentId,
	// so it naturally clears on navigation. Kept off `state` so a fresh container (refresh) resets it.
	keepEmptyTriggerForContentId: string | null = null;

	setKeepEmptyTriggerForContentId = (contentId: string | null): void => {
		this.keepEmptyTriggerForContentId = contentId;
	};

	constructor(ssrContentStateData?: any) {
		super();

		if (ssrContentStateData) {
			this.populateState(ssrContentStateData as EditorContentStateQuery$data);
		}
	}

	populateState(ssrContentStateData: EditorContentStateQuery$data): void {
		const newContentStateValues = Object.assign({}, defaultValues);
		newContentStateValues.shouldRunContentStateEffect = false;
		newContentStateValues.contentState = ssrContentStateData?.singleContent?.contentState || null;
		newContentStateValues.contentStateLastUpdated =
			ssrContentStateData?.singleContent?.contentStateLastUpdated?.value || null;
		newContentStateValues.defaultStatesArray =
			ssrContentStateData?.availableContentStates?.spaceContentStates?.filter((i) => !!i) || [];
		newContentStateValues.customStatesArray =
			ssrContentStateData?.availableContentStates?.customContentStates?.filter((i) => !!i) || [];
		// If an admin has turned off a toggle, BE returns us the value as null
		// If an empty array is returned [], means the feature is still enabled - there's just no values in there.
		// if both return as null it means the admin disabled content states
		const isSuggestedContentStatesDisabled =
			!ssrContentStateData?.availableContentStates?.spaceContentStates;
		const isCustomContentStatesDisabled =
			!ssrContentStateData?.availableContentStates?.customContentStates;
		const isContentStatesDisabled =
			isSuggestedContentStatesDisabled && isCustomContentStatesDisabled;
		if (isContentStatesDisabled) {
			newContentStateValues.isContentStateEnabled = false;
		} else if (isSuggestedContentStatesDisabled) {
			// if spaceContentStates is null, admin disabled suggested
			newContentStateValues.isSuggestedEnabled = false;
		} else if (isCustomContentStatesDisabled) {
			// if customContentStates is null, admin disabled custom
			newContentStateValues.isCustomEnabled = false;
		}

		this.state = newContentStateValues;
	}

	setContentState = (contentState: ContentState | null): Promise<void> =>
		this.setState({ contentState });
	setShouldRunContentStateEffect = (shouldRunContentStateEffect: boolean): Promise<void> =>
		this.setState({ shouldRunContentStateEffect });
	setDefaultStatesArray = (defaultStatesArray: (ContentState | null)[]): Promise<void> =>
		this.setState({ defaultStatesArray });
	setCustomStatesArray = (customStatesArray: (ContentState | null)[]): Promise<void> =>
		this.setState({ customStatesArray });
	setIsContentStateEnabled = (isContentStateEnabled: boolean): Promise<void> =>
		this.setState({ isContentStateEnabled });
	setIsSuggestedEnabled = (isSuggestedEnabled: boolean): Promise<void> =>
		this.setState({ isSuggestedEnabled });
	setIsCustomEnabled = (isCustomEnabled: boolean): Promise<void> =>
		this.setState({ isCustomEnabled });
	setIsUpdating = (isUpdating: boolean): Promise<void> => this.setState({ isUpdating });
	setLastFetchedIsDraft = (lastFetchedIsDraft: boolean | null): Promise<void> =>
		this.setState({ lastFetchedIsDraft });
	setLastFetchedContentId = (lastFetchedContentId: string | null): Promise<void> =>
		this.setState({ lastFetchedContentId });
	setInitialValues = (contentStateState: ContentStateState): Promise<void> =>
		this.setState(contentStateState);
	resetToDefaults = (): Promise<void> => this.setState({ ...defaultValues });
}
